import createContext, { Action } from "./createDataContext";
import { DispatchAction } from "./createDataContext";

/**
 * ModalCoordinator — small global registry used to coordinate overlapping
 * modals (MUI Dialog/Drawer) on the dashboard.
 *
 * Why this exists:
 *   On the dashboard, several modals can coexist at the DOM level: the AI
 *   drawer (persistent), the AI fab (fixed z-index 9999999999999), and any
 *   task-detail / start-process / sequence-flow dialog opened by the page.
 *   Because the AI fab is rendered above every backdrop, the user can tap
 *   it while another dialog is open. The AiDrawer then appears *behind*
 *   that dialog and the user sees nothing — they have to manually close
 *   the dialog to reach the AI.
 *
 *   This context lets each modal register an `onClose` handler when it
 *   opens. The Dashboard intercepts the AI "open" action and, before
 *   showing the AI, calls `closeAll()` so any other registered modal gets
 *   dismissed and the AI comes forward cleanly. Works on both desktop
 *   and mobile, and for any modal that opts in by calling `register`.
 */

type CloseHandler = () => void;

const REGISTER = "REGISTER";
const UNREGISTER = "UNREGISTER";

type InitialState = {
  // We intentionally store only the IDs (not the handlers) in state so that
  // re-renders triggered by unrelated state changes don't churn through
  // the map. The handler map lives in a ref-like object inside the action.
  openIds: string[];
};

const INITIAL_STATE: InitialState = {
  openIds: [],
};

const reducer = (state: InitialState, action: Action): InitialState => {
  switch (action.type) {
    case REGISTER: {
      const id = action.payload?.id as string;
      if (!id || state.openIds.includes(id)) return state;
      return { ...state, openIds: [...state.openIds, id] };
    }
    case UNREGISTER: {
      const id = action.payload?.id as string;
      if (!id) return state;
      return { ...state, openIds: state.openIds.filter((x) => x !== id) };
    }
    default: {
      return state;
    }
  }
};

// Handlers live outside React state: they are mutable closures that we
// keep in a module-scoped Map so they survive re-renders without being
// serialized into the reducer state. This also means register() can be
// called inside an effect with a stable handler reference.
const handlers: Map<string, CloseHandler> = new Map();

const registerModal: DispatchAction<
  { id: string; onClose: CloseHandler },
  () => void
> = (dispatch) => ({ id, onClose }) => {
  handlers.set(id, onClose);
  dispatch({ type: REGISTER, payload: { id } });
  return () => {
    handlers.delete(id);
    dispatch({ type: UNREGISTER, payload: { id } });
  };
};

const unregisterModal: DispatchAction<string, void> = (dispatch) => (id) => {
  handlers.delete(id);
  dispatch({ type: UNREGISTER, payload: { id } });
};

const closeAllModals: DispatchAction<undefined, void> = () => () => {
  // Snapshot first: calling a handler may cause the modal to call
  // unregister() synchronously, which mutates the Map during iteration.
  const snapshot = Array.from(handlers.entries());
  snapshot.forEach(([, close]) => {
    try {
      close();
    } catch {
      // A misbehaving handler should not block the other modals from
      // closing. Swallow the error intentionally.
    }
  });
};

const actions = {
  registerModal,
  unregisterModal,
  closeAllModals,
};

type ActionsType = typeof actions;
export type ModalCoordinatorContextType = {
  state: InitialState;
  actions: {
    [Property in keyof ActionsType]: ReturnType<ActionsType[Property]>;
  };
};

export const { Context, Provider } = createContext<
  ModalCoordinatorContextType,
  InitialState,
  typeof actions
>(reducer, actions, INITIAL_STATE);
